import { useMemo, useState } from 'react';
import { Box, Button, Paper, Stack, TextField, Typography } from '@mui/material';
import { occupantSearchEmptyNote, searchRoster } from '../../../lib/occupants';
import type { CreateRoomOccupantGuestRequest, GroupInvitee, GroupRoomOccupant } from '../../../types/groups';

/** Enough to recognise the right Sarah; more than that means keep typing. */
const MAX_RESULTS = 8;

/**
 * One control, two answers: find the guest on the trip, or make them.
 *
 * The operator asked for exactly this shape — "a link where you can search for
 * other guests or add a new one" — and the ordering matters. The search comes
 * first and the create form is reached through it, so a host who is about to
 * type a name that already exists sees it on the roster before they can make a
 * second copy of them.
 */
export default function OccupantSearch({
  roster,
  occupants,
  saving,
  onLink,
  onCreateGuest,
}: {
  roster: GroupInvitee[];
  occupants: GroupRoomOccupant[];
  saving: boolean;
  onLink: (inviteeId: string) => void;
  onCreateGuest: (body: CreateRoomOccupantGuestRequest) => void;
}) {
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const matches = useMemo(() => searchRoster(roster, occupants, query), [roster, occupants, query]);
  const shown = matches.slice(0, MAX_RESULTS);

  const openCreate = () => {
    // Carry the query across as the name. The host has already typed it once and
    // watched the roster fail to offer it.
    setFullName(query.trim());
    setCreating(true);
  };

  return (
    <Stack spacing={1.5}>
      <TextField
        size="small"
        fullWidth
        label="Add someone to this room"
        placeholder="Search guests on this trip by name, email or phone"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {shown.length > 0 ? (
        <Stack spacing={0.5}>
          {shown.map((invitee) => (
            <Box
              key={invitee.id}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" noWrap>{invitee.fullName}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {invitee.email || invitee.phone || 'No email or phone on file'}
                </Typography>
              </Box>
              <Button size="small" disabled={saving} onClick={() => onLink(invitee.id)}>
                Add
              </Button>
            </Box>
          ))}
          {matches.length > shown.length ? (
            <Typography variant="caption" color="text.secondary">
              Showing {shown.length} of {matches.length} — keep typing to narrow it down.
            </Typography>
          ) : null}
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary">
          {occupantSearchEmptyNote(roster, query)}
        </Typography>
      )}

      {creating ? (
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1.5}>
            <Typography variant="body2" fontWeight={700}>Add a guest who isn’t on the trip yet</Typography>
            <TextField
              size="small"
              fullWidth
              label="Full name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
            <Stack direction="row" spacing={1.5}>
              <TextField
                size="small"
                fullWidth
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                // Says out loud what the endpoint does, because a host who fears
                // duplicates leaves the address off — and an occupant with no
                // address is an occupant who is never told where they sleep.
                helperText="If this address is already on the trip we’ll link that guest instead of adding a second copy."
              />
              <TextField
                size="small"
                fullWidth
                label="Phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
              />
            </Stack>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setCreating(false)}>Cancel</Button>
              <Button
                size="small"
                variant="contained"
                disabled={saving || !fullName.trim()}
                onClick={() =>
                  onCreateGuest({
                    fullName: fullName.trim(),
                    email: email.trim() || null,
                    phone: phone.trim() || null,
                  })
                }
              >
                {saving ? 'Adding…' : 'Add to trip and room'}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      ) : (
        <Box>
          <Button size="small" disabled={saving} onClick={openCreate}>
            {query.trim() ? `Add “${query.trim()}” as a new guest` : 'Add a guest who isn’t on the trip yet'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
